import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMembers } from "../../store/actions/adminActions";
import { Button } from "react-bootstrap";

import ContentWrapper from "../utilities/ContentWrapper";

function MemberRoster() {
	const dispatch = useDispatch();
	const { members } = useSelector((state) => state.admin);
	const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

	useEffect(() => {
		dispatch(fetchMembers());
	}, [dispatch]);

	// Sort by memberId initially
	const sortedMembers = [...members].sort((a, b) => {
		let first = a.memberId.slice(-6);
		let second = b.memberId.slice(-6);
		return parseInt(first) - parseInt(second);
	});

	let flattenObj = (obj) => {
		let result = {};
		for (const i in obj) {
			if (typeof obj[i] === "object" && !Array.isArray(obj[i]) && obj[i] !== null) {
				const temp = flattenObj(obj[i]);
				for (const j in temp) {
					result[j] = temp[j];
				}
			} else {
				result[i] = obj[i];
			}
		}
		return result;
	};

	const data = [];
	sortedMembers.forEach((member, index) => {
		if (member.memberId !== "pending approval") {
			const flattenedMember = flattenObj({
				...member,
				serial: index + 1,
				salaryStr: member?.employmentDetails?.salaryStructure?.toUpperCase() || '',
				fullname: member?.name?.toUpperCase() || '',
			});
			data.push(flattenedMember);
		}
	});

	// Sorting logic
	const requestSort = (key) => {
		let direction = 'ascending';
		if (sortConfig.key === key && sortConfig.direction === 'ascending') {
			direction = 'descending';
		}
		setSortConfig({ key, direction });
	};

	const getSortedData = (data) => {
		if (!sortConfig.key) return data;

		return [...data].sort((a, b) => {
			// Handle null or undefined values
			const aValue = a[sortConfig.key] || '';
			const bValue = b[sortConfig.key] || '';

			// Numeric sorting
			if (!isNaN(aValue) && !isNaN(bValue)) {
				return sortConfig.direction === 'ascending'
					? parseFloat(aValue) - parseFloat(bValue)
					: parseFloat(bValue) - parseFloat(aValue);
			}

			// String sorting
			if (aValue < bValue) {
				return sortConfig.direction === 'ascending' ? -1 : 1;
			}
			if (aValue > bValue) {
				return sortConfig.direction === 'ascending' ? 1 : -1;
			}
			return 0;
		});
	};

	const sortedData = getSortedData(data);

	// Image formatter functions
	const renderPassport = (passportUrl) => (
		<span style={{ width: "100px", height: "100px", overflow: "hidden" }}>
			<img
				style={{ width: "100px", height: "100px", objectFit: "cover" }}
				src={passportUrl}
				alt="..."
			/>
		</span>
	);

	const renderSignature = (signatureUrl) => (
		<span style={{ width: "100px", height: "100px", overflow: "hidden" }}>
			<img
				style={{ width: "100px", height: "100px", objectFit: "cover" }}
				src={signatureUrl}
				alt="..."
			/>
		</span>
	);

	// Column definitions
	const columns = [
		{ key: "serial", label: "#", sortable: false, width: "50px" },
		{ key: "fullname", label: "Name", sortable: true, width: "200px" },
		{ key: "email", label: "Email", sortable: true, width: "200px" },
		{ key: "memberId", label: "Member Id", sortable: true, width: "200px" },
		{ key: "homeAddress", label: "Home Address", sortable: true, width: "200px" },
		{ key: "phone", label: "Phone Number", sortable: true, width: "150px" },
		{ key: "birthDate", label: "Birth Date", sortable: true, width: "200px" },
		{ key: "organisationName", label: "Name of Organisation", sortable: true, width: "200px" },
		{ key: "faculty", label: "Faculty", sortable: true, width: "200px" },
		{ key: "department", label: "Department", sortable: true, width: "200px" },
		{ key: "category", label: "Category", sortable: true, width: "200px" },
		{ key: "rank", label: "Rank", sortable: true, width: "200px" },
		{ key: "gradeLevel", label: "Grade Level", sortable: true, width: "200px" },
		{ key: "retirementDate", label: "Date of Retirement", sortable: true, width: "200px" },
		{ key: "ippisNum", label: "IPPIS Number", sortable: true, width: "200px" },
		{ key: "salaryStr", label: "Salary Structure", sortable: true, width: "200px" },
		{ key: "staffNum", label: "Staff Number", sortable: true, width: "200px" },
		{ key: "assumptionOfdutyDate", label: "Assumption of Duty", sortable: true, width: "200px" },
		{ key: "nok1", label: "NOK 1", sortable: true, width: "200px" },
		{ key: "nokAddress1", label: "NOK 1 Address", sortable: true, width: "200px" },
		{ key: "nokEmail1", label: "NOK 1 Email", sortable: true, width: "200px" },
		{ key: "relationship1", label: "NOK 1 relationship", sortable: true, width: "200px" },
		{ key: "nokPhone1", label: "NOK 1 Phone", sortable: true, width: "200px" },
		{ key: "nok2", label: "NOK 2", sortable: true, width: "200px" },
		{ key: "nokAddress2", label: "NOK 2 Address", sortable: true, width: "200px" },
		{ key: "nokEmail2", label: "NOK 2 Email", sortable: true, width: "200px" },
		{ key: "nokPhone2", label: "NOK 2 Phone", sortable: true, width: "200px" },
		{ key: "relationship2", label: "NOK 2 relationship", sortable: true, width: "200px" },
		{ key: "passport", label: "Passport", sortable: false, width: "200px", formatter: renderPassport },
		{ key: "signature", label: "Signature", sortable: false, width: "200px", formatter: renderSignature },
	];

	// Export to CSV function
	const exportToCSV = () => {
		// Filter out image columns or replace with text
		const exportColumns = columns.filter(col => col.key !== "passport" && col.key !== "signature");

		const headers = exportColumns.map(col => col.label).join(',');
		const rows = data.map(item => {
			return exportColumns.map(col => {
				// Handle commas and quotes in CSV fields
				const cellValue = item[col.key] || '';
				const formattedValue = typeof cellValue === 'string' && cellValue.includes(',')
					? `"${cellValue}"`
					: cellValue;
				return formattedValue;
			}).join(',');
		}).join('\n');

		const csvContent = `${headers}\n${rows}`;
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);

		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'members_register.csv');
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	return (
		<ContentWrapper>
			<div className="card">
				<div className="card-header">
					<h3 className="card-title">Members Register</h3>
				</div>
				<section className="content pb-3">
					<div className="card-body">
						<div style={{ textAlign: "right" }}>
							<Button variant="primary" size="lg" onClick={exportToCSV}>
								Export To CSV
							</Button>
						</div>
						<hr />
						<div
							style={{
								width: "auto",
								height: "500px",
								overflow: "auto",
							}}
						>
							<table className="table table-striped table-hover">
								<thead>
									<tr>
										{columns.map((column) => (
											<th
												key={column.key}
												style={{
													backgroundColor: "#c8e6c9",
													width: column.width,
													cursor: column.sortable ? 'pointer' : 'default',
													position: 'sticky',
													top: 0,
													zIndex: 1
												}}
												onClick={() => column.sortable ? requestSort(column.key) : null}
											>
												{column.label}
												{column.sortable && (
													<span className="ml-1">
														{sortConfig.key === column.key ? (
															sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'
														) : ''}
													</span>
												)}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{sortedData.map((member) => (
										<tr key={member._id}>
											{columns.map((column) => (
												<td key={`${member._id}-${column.key}`}>
													{column.formatter
														? column.formatter(member[column.key])
														: member[column.key]}
												</td>
											))}
										</tr>
									))}
									{sortedData.length === 0 && (
										<tr>
											<td colSpan={columns.length} className="text-center">
												No members found
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</section>
			</div>
		</ContentWrapper>
	);
}

export default MemberRoster;